import type { Appointment, CardCheckout, PaidMethod, PaymentState } from '../api/client'
import { formatPrice } from '../catalog/format'

// legacyState reads payment_state from the older payment_status, for an API
// that does not send it yet.
function legacyState(a: Appointment): PaymentState {
  if (a.package) return 'package'
  if (a.payment_status === 'refunded') return 'refunded'
  if (a.refunded_pence > 0) return 'part_refunded'
  switch (a.payment_status) {
    case 'paid':
      return 'paid'
    case 'outstanding':
      return 'to_pay'
    case 'part_paid':
      return a.status === 'completed' && a.owed_pence > 0 ? 'to_pay' : 'deposit_paid'
    default:
      return 'none'
  }
}

// paymentLine is the appointment sheet's payment, after the price: the same
// words as the row's tag (paymentStateLabel on the API's payment_state, QA
// 2026-10-14), with the amounts the sheet has room for: "To pay",
// "£20 deposit paid · £79 to pay", "Part refunded £12.50". A lost or open
// dispute, or a double payment, says so first. Empty when there is nothing to say (a
// cancelled or no-show appointment with nothing paid, or a free one).
export function paymentLine(a: Appointment): string {
  if (a.dispute_outcome === 'lost') return 'Dispute lost'
  if (a.disputed) return 'Disputed'
  if (a.paid_twice) return 'Paid twice — refund?'
  const state = a.payment_state ?? legacyState(a)
  const off = a.status === 'cancelled' || a.status === 'no_show'
  const deposit = (paid: number) => {
    const line = `${formatPrice(paid)} deposit paid`
    return !off && a.owed_pence > 0 ? `${line} · ${formatPrice(a.owed_pence)} to pay` : line
  }
  switch (state) {
    case 'part_refunded':
      return a.refunded_pence > 0 ? `Part refunded ${formatPrice(a.refunded_pence)}` : paymentStateLabel(state)
    case 'paid':
      return paymentStateLabel(state, a.paid_method ?? (a.card_payment_id ? 'pay_link' : null))
    case 'deposit_paid':
      return a.paid_pence > 0 ? deposit(a.paid_pence) : paymentStateLabel(state)
    case 'to_pay':
      return a.paid_pence > a.refunded_pence ? deposit(a.paid_pence - a.refunded_pence) : 'To pay'
    case 'none':
      return off || a.total_price_pence <= 0 ? '' : 'To pay'
    default:
      return paymentStateLabel(state)
  }
}

// statusLabel names an appointment's own state where a screen shows it.
export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
    case 'no_show':
      return 'No-show'
    default:
      return 'Booked'
  }
}

// paidBy words how something was paid, after "paid": "by card", "in cash".
export function paidBy(method: PaidMethod | null | undefined): string {
  switch (method) {
    case 'cash':
      return 'in cash'
    case 'card':
    case 'pay_link':
      return 'by card'
    case 'bank_transfer':
      return 'by bank transfer'
    case 'package':
      return 'with a package'
    default:
      return ''
  }
}

// paymentStateLabel is the one wording of the API's payment_state (A1), for
// rows that show it (Client history; C1 brings it to Today and Calendar).
// Empty when there is nothing to say (nothing paid and nothing due yet).
export function paymentStateLabel(state: PaymentState | undefined, method?: PaidMethod | null, duePence?: number): string {
  switch (state) {
    case 'package':
      // True before the visit and after it (QA 2026-10-14).
      return 'Package session'
    case 'dispute_lost':
      // The client's bank took the card payment back; the money is gone.
      return 'Dispute lost'
    case 'refunded':
      return 'Refunded'
    case 'part_refunded':
      return 'Part refunded'
    case 'paid': {
      const how = paidBy(method)
      return how ? `Paid ${how}` : 'Paid'
    }
    case 'to_pay':
      return duePence && duePence > 0 ? `To pay ${formatPrice(duePence)}` : 'To pay'
    case 'deposit_paid':
      return 'Deposit paid'
    case 'paid_before':
      // Imported history: settled in her old system (Fresha), nothing to take.
      return 'Paid before Harmovi'
    default:
      return ''
  }
}

// completedMessage is the toast after Complete when nothing more was owed,
// so no method was asked: what paid for it, if anything.
export function completedMessage(a: Pick<Appointment, 'payment_state' | 'paid_method' | 'deposit_pence' | 'total_price_pence'>): string {
  if (a.payment_state === 'package' || a.paid_method === 'package') return 'Completed · paid with a package'
  if (a.deposit_pence && a.deposit_pence >= a.total_price_pence && a.paid_method === 'pay_link') return 'Completed · paid by deposit'
  const how = paidBy(a.paid_method)
  return how && (a.payment_state === 'paid' || a.payment_state === undefined) ? `Completed · paid ${how}` : 'Completed'
}

// afterCancel decides what to do with the checkout the cancel call returned.
// Cancelling a link that was already paid answers 200 with the link's real
// state, so the answer, not the click, says what happened: cancelled (or
// expired) closes, paid is shown as paid, and a still-open answer means the
// payment is being settled, so keep polling until the webhook lands.
export function afterCancel(status: CardCheckout['status']): 'close' | 'paid' | 'keep-polling' {
  if (status === 'paid') return 'paid'
  if (status === 'open') return 'keep-polling'
  return 'close'
}

export const CHECKOUT_POLL_MS = 3000
// A sheet left open overnight once asked about the same link every few
// seconds for eleven hours (2026-10-25), so a stretch of checking ends after
// this long and she can check again.
export const CHECKOUT_POLL_LIMIT_MS = 10 * 60 * 1000

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

// pollCheckout asks for the checkout's state every few seconds and hands each
// answer to onUpdate, stopping on its own once the link is no longer open.
// It pauses while the window is hidden and checks at once when it is shown
// again. After ten minutes of checking (counted afresh each time the window
// comes back) it stops and calls onTimeUp. now checks at once instead of
// waiting for the first tick. The returned function stops it early (unmount)
// and discards any answer still in flight. A failed request is skipped; the
// next tick tries again.
export function pollCheckout(
  get: (id: string) => Promise<CardCheckout>,
  checkoutId: string,
  onUpdate: (checkout: CardCheckout) => void,
  { onTimeUp, now = false }: { onTimeUp?: () => void; now?: boolean } = {},
): () => void {
  let stopped = false
  let asking = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let since = Date.now()

  const clear = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
  }

  const stop = () => {
    stopped = true
    clear()
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
  }

  const schedule = () => {
    if (!isHidden()) timer = setTimeout(tick, CHECKOUT_POLL_MS)
  }

  const tick = async () => {
    timer = undefined
    asking = true
    let latest: CardCheckout | null = null
    try {
      latest = await get(checkoutId)
    } catch {
      // try again on the next tick
    }
    asking = false
    if (stopped) return
    if (latest) {
      onUpdate(latest)
      if (latest.status !== 'open') {
        stop()
        return
      }
    }
    if (Date.now() - since >= CHECKOUT_POLL_LIMIT_MS) {
      stop()
      onTimeUp?.()
      return
    }
    schedule()
  }

  function onVisibility() {
    if (stopped) return
    if (isHidden()) {
      clear()
      return
    }
    since = Date.now()
    if (!asking && timer === undefined) void tick()
  }

  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)
  if (now && !isHidden()) void tick()
  else schedule()
  return stop
}
